import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, MessageCircle, BadgeCheck, MapPin, ArrowRight, Star } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { usePublicSettings } from "@/context/SettingsContext";
import ApartmentCard from "@/components/ApartmentCard";
import SectionHeading from "@/components/SectionHeading";

const HERO = "https://images.pexels.com/photos/8082227/pexels-photo-8082227.jpeg";
const CITY = "https://images.unsplash.com/photo-1681844931449-e0992a27d157?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const { t } = useLang();
  const s = usePublicSettings();
  const [apartments, setApartments] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/apartments").then((r) => setApartments(r.data.slice(0, 3))).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const benefits = [
    { icon: KeyRound, key: "1" },
    { icon: MessageCircle, key: "2" },
    { icon: BadgeCheck, key: "3" },
    { icon: MapPin, key: "4" },
  ];

  const wa = (s.whatsapp_number || "").replace(/[^0-9]/g, "");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <img src={HERO} alt="Fiorucci Apartments" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
          <p className="overline text-white/80 mb-6 fade-up d-1">{t("hero.overline")}</p>
          <h1 className="font-serif font-light text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight max-w-4xl fade-up d-2">
            {t("hero.title")}
          </h1>
          <p className="mt-7 text-white/85 text-lg max-w-xl fade-up d-3">{t("hero.subtitle")}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-up d-4">
            <Link
              to="/apartments"
              data-testid="hero-cta-discover"
              className="inline-flex items-center justify-center gap-2 bg-[#FDFBF7] text-petrol px-8 py-4 rounded-sm hover:bg-white transition-all duration-300 group"
            >
              {t("hero.cta_discover")}
              <ArrowRight size={18} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* EDITORIAL RIBBON / BENEFITS marquee */}
      <section className="bg-beige border-y border-line overflow-hidden py-5">
        <div className="marquee-track">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center">
              {["check-in semplice", "assistenza rapida", "appartamenti selezionati", "posizione strategica", "self check-in"].map(
                (w, i) => (
                  <span key={`${dup}-${i}`} className="flex items-center">
                    <span className="font-serif italic text-2xl text-petrol px-8">{w}</span>
                    <span className="text-gold">✦</span>
                  </span>
                )
              )}
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <SectionHeading overline={t("benefits.overline")} title={t("benefits.title")} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line mt-16 border border-line">
          {benefits.map(({ icon: Icon, key }) => (
            <div key={key} data-testid={`benefit-${key}`} className="bg-cream p-8 md:p-10 group hover:bg-beige transition-colors duration-500">
              <Icon size={30} strokeWidth={1.25} className="text-gold mb-6" />
              <h3 className="font-serif text-2xl text-petrol mb-3">{t(`benefits.${key}.title`)}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{t(`benefits.${key}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* APARTMENTS PREVIEW */}
      <section className="bg-beige/50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <SectionHeading overline={t("apartments.overline")} title={t("apartments.preview_title")} />
            <Link to="/apartments" className="inline-flex items-center gap-2 text-petrol link-underline self-start md:self-auto" data-testid="home-view-all">
              {t("apartments.view_all")} <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {apartments.map((apt, i) => (
              <ApartmentCard key={apt.id} apt={apt} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <SectionHeading overline={t("reviews.overline")} title={t("reviews.title")} />
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            {reviews.slice(0, 4).map((rv) => (
              <div key={rv.id} data-testid={`review-${rv.id}`} className="border border-line bg-card p-8 md:p-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex gap-1">
                    {[...Array(rv.rating)].map((_, i) => (
                      <Star key={i} size={16} className="fill-gold text-gold" />
                    ))}
                  </div>
                  {rv.source && rv.source !== "sito" && (
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground border border-line rounded-full px-2.5 py-1">
                      da {rv.source === "airbnb" ? "Airbnb" : rv.source === "booking" ? "Booking" : "Altro"}
                    </span>
                  )}
                </div>
                <p className="font-serif text-2xl text-petrol leading-snug mb-6">“{rv.text}”</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-petrol">{rv.guest_name}</span>
                  <span className="text-muted-foreground">{[rv.country, rv.apartment_name].filter(Boolean).join(" · ")}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <img src={CITY} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-petrol/85" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-28 md:py-36 text-center">
          <h2 className="font-serif font-light text-[#FDFBF7] text-4xl md:text-6xl leading-tight tracking-tight">
            {t("cta.title")}
          </h2>
          <p className="mt-6 text-white/75 text-lg max-w-xl mx-auto">{t("cta.subtitle")}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/apartments"
              data-testid="cta-book"
              className="inline-flex items-center justify-center gap-2 bg-gold text-petrol px-8 py-4 rounded-sm hover:bg-[#c4a030] transition-colors font-medium"
            >
              {t("cta.book")}
            </Link>
            {wa && (
              <a
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noreferrer"
                data-testid="cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-8 py-4 rounded-sm hover:bg-white/10 transition-colors"
              >
                <MessageCircle size={18} strokeWidth={1.5} /> {t("cta.whatsapp")}
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
